import { useState, useCallback } from 'react';
import MathRenderer from '../../../components/base/MathRenderer';

// ─── Template Schema ──────────────────────────────────────────────────────────

interface FieldDef {
  key: string;
  label: string;
  placeholder: string;
  type?: 'text' | 'select';
  options?: string[];
}

interface FormulaTemplate {
  id: string;
  name: string;
  latex: string;
  desc: string;
  fields: FieldDef[];
  build: (v: Record<string, string>) => string;
}

interface Category {
  id: string;
  label: string;
  icon: string;
  gradient: string;
  badge: string;
  templates: FormulaTemplate[];
}

// ─── Colour helpers ───────────────────────────────────────────────────────────

const UNIT_OPTS: Record<string, string[]> = {
  length: ['km', 'miles', 'm', 'feet', 'inches', 'cm', 'mm', 'yards'],
  mass: ['kg', 'lbs', 'g', 'oz', 'stones', 'tonnes'],
  temperature: ['Celsius', 'Fahrenheit', 'Kelvin'],
  volume: ['liters', 'gallons', 'ml', 'pints', 'cups', 'fl oz'],
  speed: ['km/h', 'mph', 'm/s', 'knots'],
  area: ['m2', 'ft2', 'km2', 'acres', 'hectares', 'sq miles'],
  energy: ['joules', 'kJ', 'calories', 'kcal', 'kWh', 'BTU'],
  pressure: ['atm', 'psi', 'Pa', 'kPa', 'bar', 'mmHg'],
};

// ─── Categories + Templates ───────────────────────────────────────────────────

const CATEGORIES: Category[] = [
  {
    id: 'calculus',
    label: 'Calculus',
    icon: 'ri-function-line',
    gradient: 'from-violet-500 to-fuchsia-500',
    badge: 'bg-violet-100 text-violet-700',
    templates: [
      {
        id: 'derivative',
        name: 'Derivative',
        latex: '\\dfrac{d}{dx}\\,f(x)',
        desc: 'Differentiate any expression w.r.t. x',
        fields: [{ key: 'f', label: 'Expression f(x)', placeholder: 'x^3 * sin(x)' }],
        build: v => `derivative of ${v.f || 'x^2'}`,
      },
      {
        id: 'def_integral',
        name: 'Definite Integral',
        latex: '\\int_a^b f(x)\\,dx',
        desc: 'Compute area under a curve between two bounds',
        fields: [
          { key: 'f', label: 'Expression f(x)', placeholder: 'x^2' },
          { key: 'a', label: 'Lower bound a', placeholder: '0' },
          { key: 'b', label: 'Upper bound b', placeholder: '3' },
        ],
        build: v => `integrate ${v.f || 'x^2'} from ${v.a || '0'} to ${v.b || '3'} dx`,
      },
      {
        id: 'indef_integral',
        name: 'Indefinite Integral',
        latex: '\\int f(x)\\,dx',
        desc: 'Find the antiderivative + plot the function',
        fields: [{ key: 'f', label: 'Expression f(x)', placeholder: 'sin(x) * cos(x)' }],
        build: v => `integrate ${v.f || 'sin(x)'} dx`,
      },
      {
        id: 'limit',
        name: 'Limit',
        latex: '\\lim_{x \\to a}\\,f(x)',
        desc: 'Evaluate a limit as x approaches a value',
        fields: [
          { key: 'f', label: 'Expression f(x)', placeholder: 'sin(x)/x' },
          { key: 'a', label: 'x approaches', placeholder: '0' },
        ],
        build: v => `limit as x->${v.a || '0'} of ${v.f || 'sin(x)/x'}`,
      },
      {
        id: 'taylor',
        name: 'Taylor / Maclaurin Series',
        latex: '\\sum_{n=0}^{\\infty}\\frac{f^{(n)}(a)}{n!}(x-a)^n',
        desc: 'Expand a function as a power series',
        fields: [
          { key: 'f', label: 'Function f(x)', placeholder: 'e^x' },
          { key: 'a', label: 'Center point a (0 = Maclaurin)', placeholder: '0' },
        ],
        build: v => `Taylor series of ${v.f || 'e^x'} at x=${v.a || '0'}`,
      },
    ],
  },
  {
    id: 'algebra',
    label: 'Algebra',
    icon: 'ri-calculator-line',
    gradient: 'from-emerald-500 to-teal-500',
    badge: 'bg-emerald-100 text-emerald-700',
    templates: [
      {
        id: 'quadratic',
        name: 'Solve Quadratic',
        latex: 'ax^2 + bx + c = 0',
        desc: 'Find roots using the quadratic formula',
        fields: [
          { key: 'a', label: 'Coefficient a (x²)', placeholder: '1' },
          { key: 'b', label: 'Coefficient b (x)', placeholder: '-5' },
          { key: 'c', label: 'Constant c', placeholder: '6' },
        ],
        build: v => {
          const a = v.a || '1', b = v.b || '-5', c = v.c || '6';
          const bNum = parseFloat(b);
          const cNum = parseFloat(c);
          const bStr = !isNaN(bNum) ? (bNum >= 0 ? `+ ${b}` : `- ${Math.abs(bNum)}`) : `+ (${b})`;
          const cStr = !isNaN(cNum) ? (cNum >= 0 ? `+ ${c}` : `- ${Math.abs(cNum)}`) : `+ (${c})`;
          return `solve ${a}x^2 ${bStr}x ${cStr} = 0`;
        },
      },
      {
        id: 'solve_eq',
        name: 'Solve Equation',
        latex: 'f(x) = g(x)',
        desc: 'Solve for x given left and right sides',
        fields: [
          { key: 'lhs', label: 'Left side', placeholder: 'x^3 - 6x^2' },
          { key: 'rhs', label: 'Right side', placeholder: '0' },
        ],
        build: v => `solve ${v.lhs || 'x^2 - 4'} = ${v.rhs || '0'}`,
      },
      {
        id: 'factor',
        name: 'Factor Polynomial',
        latex: 'p(x) = a(x-r_1)(x-r_2)\\cdots',
        desc: 'Find roots and write factored form',
        fields: [{ key: 'expr', label: 'Polynomial expression', placeholder: 'x^3 - 6x^2 + 11x - 6' }],
        build: v => `factor ${v.expr || 'x^2 - 5x + 6'}`,
      },
      {
        id: 'simplify',
        name: 'Simplify Expression',
        latex: '\\text{simplify}\\;f(x)',
        desc: 'Reduce an algebraic expression to simplest form',
        fields: [{ key: 'expr', label: 'Expression', placeholder: '(x^2 - 4)/(x - 2)' }],
        build: v => `simplify ${v.expr || '(x^2 - 4)/(x - 2)'}`,
      },
      {
        id: 'expand',
        name: 'Expand',
        latex: '(a + b)^n',
        desc: 'Expand a product or power into sum of terms',
        fields: [{ key: 'expr', label: 'Expression to expand', placeholder: '(x + 3)^4' }],
        build: v => `expand ${v.expr || '(x + 1)^5'}`,
      },
    ],
  },
  {
    id: 'geometry',
    label: 'Geometry',
    icon: 'ri-shape-line',
    gradient: 'from-orange-500 to-amber-500',
    badge: 'bg-orange-100 text-orange-700',
    templates: [
      {
        id: 'circle_area',
        name: 'Circle Area',
        latex: 'A = \\pi r^2',
        desc: 'Area enclosed by a circle',
        fields: [{ key: 'r', label: 'Radius r', placeholder: '7' }],
        build: v => `area of a circle with radius ${v.r || '7'}`,
      },
      {
        id: 'sphere_vol',
        name: 'Sphere Volume',
        latex: 'V = \\tfrac{4}{3}\\pi r^3',
        desc: 'Volume of a solid sphere',
        fields: [{ key: 'r', label: 'Radius r', placeholder: '5' }],
        build: v => `volume of a sphere radius ${v.r || '5'}`,
      },
      {
        id: 'pythagorean',
        name: 'Pythagorean Theorem',
        latex: 'c = \\sqrt{a^2 + b^2}',
        desc: 'Find the hypotenuse of a right triangle',
        fields: [
          { key: 'a', label: 'Side a', placeholder: '3' },
          { key: 'b', label: 'Side b', placeholder: '4' },
        ],
        build: v => `pythagorean theorem a=${v.a || '3'} b=${v.b || '4'}`,
      },
      {
        id: 'cylinder_sa',
        name: 'Cylinder Surface Area',
        latex: 'SA = 2\\pi r(r + h)',
        desc: 'Total surface area of a cylinder',
        fields: [
          { key: 'r', label: 'Radius r', placeholder: '3' },
          { key: 'h', label: 'Height h', placeholder: '10' },
        ],
        build: v => `surface area of a cylinder r=${v.r || '3'} h=${v.h || '10'}`,
      },
      {
        id: 'triangle_area',
        name: 'Triangle Area',
        latex: 'A = \\tfrac{1}{2}bh',
        desc: 'Area of a triangle given base and height',
        fields: [
          { key: 'b', label: 'Base', placeholder: '8' },
          { key: 'h', label: 'Height', placeholder: '6' },
        ],
        build: v => `area of triangle base=${v.b || '8'} height=${v.h || '6'}`,
      },
    ],
  },
  {
    id: 'physics',
    label: 'Physics',
    icon: 'ri-rocket-line',
    gradient: 'from-sky-500 to-cyan-500',
    badge: 'bg-sky-100 text-sky-700',
    templates: [
      {
        id: 'newton2',
        name: "Newton's 2nd Law",
        latex: 'F = ma',
        desc: 'Force = mass × acceleration',
        fields: [
          { key: 'm', label: 'Mass m (kg)', placeholder: '5' },
          { key: 'a', label: 'Acceleration a (m/s²)', placeholder: '9.8' },
        ],
        build: v => `F = ma, m=${v.m || '5'} a=${v.a || '9.8'}`,
      },
      {
        id: 'kinetic',
        name: 'Kinetic Energy',
        latex: 'KE = \\tfrac{1}{2}mv^2',
        desc: 'Energy of a moving object',
        fields: [
          { key: 'm', label: 'Mass m (kg)', placeholder: '2' },
          { key: 'v', label: 'Velocity v (m/s)', placeholder: '30' },
        ],
        build: v => `kinetic energy m=${v.m || '2'} v=${v.v || '30'}`,
      },
      {
        id: 'potential',
        name: 'Potential Energy',
        latex: 'PE = mgh',
        desc: 'Gravitational potential energy',
        fields: [
          { key: 'm', label: 'Mass m (kg)', placeholder: '10' },
          { key: 'h', label: 'Height h (m)', placeholder: '5' },
        ],
        build: v => `potential energy m=${v.m || '10'} h=${v.h || '5'}`,
      },
      {
        id: 'ohms',
        name: "Ohm's Law",
        latex: 'V = IR',
        desc: 'Voltage, current, resistance relationship',
        fields: [
          { key: 'V', label: 'Voltage V (volts)', placeholder: '12' },
          { key: 'R', label: 'Resistance R (ohms)', placeholder: '4' },
        ],
        build: v => `Ohm's law V=${v.V || '12'} R=${v.R || '4'}`,
      },
      {
        id: 'momentum',
        name: 'Momentum',
        latex: 'p = mv',
        desc: 'Linear momentum of an object',
        fields: [
          { key: 'm', label: 'Mass m (kg)', placeholder: '70' },
          { key: 'v', label: 'Velocity v (m/s)', placeholder: '5' },
        ],
        build: v => `momentum m=${v.m || '70'} v=${v.v || '5'}`,
      },
    ],
  },
  {
    id: 'stats',
    label: 'Statistics',
    icon: 'ri-bar-chart-2-line',
    gradient: 'from-rose-500 to-pink-500',
    badge: 'bg-rose-100 text-rose-700',
    templates: [
      {
        id: 'mean',
        name: 'Mean',
        latex: '\\bar{x} = \\dfrac{\\sum x_i}{n}',
        desc: 'Average of a dataset',
        fields: [{ key: 'vals', label: 'Values (comma-separated)', placeholder: '3, 7, 2, 9, 4, 6' }],
        build: v => `mean of {${v.vals || '3, 7, 2, 9, 4, 6'}}`,
      },
      {
        id: 'stddev',
        name: 'Standard Deviation',
        latex: '\\sigma = \\sqrt{\\dfrac{\\sum(x_i-\\bar{x})^2}{n}}',
        desc: 'Measure of spread in a dataset',
        fields: [{ key: 'vals', label: 'Values (comma-separated)', placeholder: '2, 4, 4, 4, 5, 5, 7, 9' }],
        build: v => `standard deviation of {${v.vals || '2, 4, 4, 4, 5, 5, 7, 9'}}`,
      },
      {
        id: 'normal_dist',
        name: 'Normal Distribution',
        latex: 'X \\sim \\mathcal{N}(\\mu,\\, \\sigma^2)',
        desc: 'Bell curve statistics and 68-95-99.7 rule',
        fields: [
          { key: 'mu', label: 'Mean μ', placeholder: '0' },
          { key: 'sigma', label: 'Std deviation σ', placeholder: '1' },
        ],
        build: v => `normal distribution mean=${v.mu || '0'} sigma=${v.sigma || '1'}`,
      },
      {
        id: 'binomial',
        name: 'Binomial Distribution',
        latex: 'X \\sim B(n,\\, p)',
        desc: 'Probability of k successes in n trials',
        fields: [
          { key: 'n', label: 'Number of trials n', placeholder: '10' },
          { key: 'p', label: 'Probability p (0–1)', placeholder: '0.5' },
        ],
        build: v => `binomial distribution n=${v.n || '10'} p=${v.p || '0.5'}`,
      },
    ],
  },
  {
    id: 'chemistry',
    label: 'Chemistry',
    icon: 'ri-flask-line',
    gradient: 'from-lime-500 to-green-500',
    badge: 'bg-lime-100 text-lime-700',
    templates: [
      {
        id: 'balance_eq',
        name: 'Balance Equation',
        latex: 'a\\text{A} + b\\text{B} \\rightarrow c\\text{C} + d\\text{D}',
        desc: 'Balance a chemical equation and get mole ratios',
        fields: [
          { key: 'reactants', label: 'Reactants (use + between compounds)', placeholder: 'H2 + O2' },
          { key: 'products', label: 'Products (use + between compounds)', placeholder: 'H2O' },
        ],
        build: (v: Record<string, string>) => `balance ${v.reactants || 'H2 + O2'} → ${v.products || 'H2O'}`,
      },
      {
        id: 'molar_mass',
        name: 'Molar Mass',
        latex: 'M = \\sum n_i \\cdot A_i',
        desc: 'Calculate molecular weight of a compound',
        fields: [{ key: 'formula', label: 'Chemical formula', placeholder: 'H2SO4' }],
        build: (v: Record<string, string>) => `molar mass of ${v.formula || 'H2SO4'}`,
      },
      {
        id: 'ph_calc',
        name: 'pH Calculation',
        latex: 'pH = -\\log_{10}[H^+]',
        desc: 'Find pH of a strong acid solution',
        fields: [{ key: 'conc', label: 'Concentration [H⁺] in mol/L', placeholder: '0.001' }],
        build: (v: Record<string, string>) => `pH of ${v.conc || '0.001'} M solution`,
      },
      {
        id: 'combustion',
        name: 'Combustion Reaction',
        latex: '\\text{C}_x\\text{H}_y + O_2 \\rightarrow CO_2 + H_2O',
        desc: 'Balance a hydrocarbon combustion equation',
        fields: [{ key: 'fuel', label: 'Fuel formula', placeholder: 'CH4' }],
        build: (v: Record<string, string>) => `balance ${v.fuel || 'CH4'} + O2 → CO2 + H2O`,
      },
    ],
  },
  {
    label: 'Convert',
    icon: 'ri-arrow-left-right-line',
    gradient: 'from-teal-500 to-emerald-500',
    badge: 'bg-teal-100 text-teal-700',
    templates: (['length', 'mass', 'temperature', 'volume', 'speed', 'area', 'energy', 'pressure'] as const).map(dim => ({
      id: `convert_${dim}`,
      name: dim.charAt(0).toUpperCase() + dim.slice(1),
      latex: dim === 'temperature' ? 'T_{out} = f(T_{in})'
        : dim === 'speed' ? 'v_{out} = v_{in} \\times \\frac{F_{in}}{F_{out}}'
        : 'x_{out} = x_{in} \\times \\frac{F_{in}}{F_{out}}',
      desc: `Convert ${dim} between common units`,
      fields: [
        { key: 'value', label: 'Value', placeholder: dim === 'temperature' ? '100' : '5' },
        { key: 'from', label: 'From unit', placeholder: UNIT_OPTS[dim]?.[0] ?? 'km', type: 'select' as const, options: UNIT_OPTS[dim] ?? [] },
        { key: 'to', label: 'To unit', placeholder: UNIT_OPTS[dim]?.[1] ?? 'miles', type: 'select' as const, options: UNIT_OPTS[dim] ?? [] },
      ],
      build: (v: Record<string, string>) => `convert ${v.value || '5'} ${v.from || UNIT_OPTS[dim]?.[0] || 'km'} to ${v.to || UNIT_OPTS[dim]?.[1] || 'miles'}`,
    })),
  },
];

// ─── Component ────────────────────────────────────────────────────────────────

interface FormulaBuilderProps {
  show: boolean;
  onClose: () => void;
  onInsert: (query: string) => void;
  onSolve: (query: string) => void;
}

export default function FormulaBuilder({ show, onClose, onInsert, onSolve }: FormulaBuilderProps) {
  const [activeCat, setActiveCat] = useState('calculus');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [fieldValues, setFieldValues] = useState<Record<string, Record<string, string>>>({});
  const [search, setSearch] = useState('');

  const cat = CATEGORIES.find(c => c.id === activeCat) ?? CATEGORIES[0];

  const filteredTemplates = search.trim()
    ? CATEGORIES.flatMap(c => c.templates.map(t => ({ ...t, catId: c.id })))
        .filter(t => t.name.toLowerCase().includes(search.toLowerCase()) || t.desc.toLowerCase().includes(search.toLowerCase()))
    : cat.templates.map(t => ({ ...t, catId: activeCat }));

  const setField = useCallback((templateId: string, key: string, value: string) => {
    setFieldValues(prev => ({
      ...prev,
      [templateId]: { ...(prev[templateId] ?? {}), [key]: value },
    }));
  }, []);

  const buildQuery = useCallback((template: FormulaTemplate): string => {
    return template.build(fieldValues[template.id] ?? {});
  }, [fieldValues]);

  const handleInsert = useCallback((template: FormulaTemplate) => {
    onInsert(buildQuery(template));
    onClose();
  }, [buildQuery, onInsert, onClose]);

  const handleSolve = useCallback((template: FormulaTemplate) => {
    const q = buildQuery(template);
    onSolve(q);
    onClose();
  }, [buildQuery, onSolve, onClose]);

  return (
    <>
      {show && <div className="fixed inset-0 bg-black/20 backdrop-blur-[1px] z-40" onClick={onClose} />}
      <div
        className={`fixed top-0 right-0 h-full w-[380px] bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-700 z-50 flex flex-col transition-transform duration-300 ease-in-out ${show ? 'translate-x-0' : 'translate-x-full'}`}
        style={{ boxShadow: '-8px 0 32px rgba(0,0,0,0.14)' }}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-600 to-emerald-600 px-5 py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center">
              <i className="ri-function-add-line text-white text-lg"></i>
            </div>
            <div>
              <h3 className="text-white font-bold text-sm">Formula Builder</h3>
              <p className="text-teal-100 text-xs">Fill in values → Insert or Solve</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/20 hover:bg-white/30 text-white cursor-pointer transition-colors">
            <i className="ri-close-line"></i>
          </button>
        </div>

        {/* Search */}
        <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700 flex-shrink-0">
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm"></i>
            <input
              type="text"
              placeholder="Search formulas…"
              value={search}
              onChange={e => { setSearch(e.target.value); setExpanded(null); }}
              className="w-full pl-8 pr-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent"
            />
          </div>
        </div>

        {/* Category tabs */}
        {!search && (
          <div className="flex overflow-x-auto border-b border-slate-100 dark:border-slate-700 flex-shrink-0" style={{ scrollbarWidth: 'none' }}>
            {CATEGORIES.map(c => (
              <button
                key={c.id}
                onClick={() => { setActiveCat(c.id); setExpanded(null); }}
                className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-2.5 text-[11px] font-semibold transition-all cursor-pointer whitespace-nowrap border-b-2 ${
                  activeCat === c.id
                    ? 'border-teal-500 text-teal-700 dark:text-teal-400 bg-teal-50/60 dark:bg-teal-900/20'
                    : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800'
                }`}
              >
                <i className={`${c.icon} text-[10px]`}></i>
                {c.label}
              </button>
            ))}
          </div>
        )}

        {/* Template list */}
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {filteredTemplates.length === 0 && (
            <div className="text-center py-10 text-slate-400 text-sm">
              <i className="ri-search-line text-2xl mb-2 block"></i>
              No formulas found for &ldquo;{search}&rdquo;
            </div>
          )}

          {filteredTemplates.map((tpl) => {
            const category = CATEGORIES.find(c => c.id === (tpl as typeof tpl & { catId: string }).catId) ?? cat;
            const isOpen = expanded === tpl.id;
            const vals = fieldValues[tpl.id] ?? {};
            const builtQuery = tpl.build(vals);

            return (
              <div
                key={tpl.id}
                className={`rounded-xl border overflow-hidden transition-all duration-200 ${isOpen ? 'border-teal-300 dark:border-teal-700' : 'border-slate-200 dark:border-slate-700 hover:border-teal-200 dark:hover:border-teal-800'}`}
              >
                {/* Card header */}
                <button
                  onClick={() => setExpanded(isOpen ? null : tpl.id)}
                  className="w-full flex items-center gap-3 px-4 py-3 cursor-pointer bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-750 transition-colors text-left"
                >
                  <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${category.gradient} flex items-center justify-center flex-shrink-0`}>
                    <i className={`${category.icon} text-white text-xs`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-800 dark:text-slate-100 text-xs">{tpl.name}</div>
                    <div className="text-[10px] text-slate-400 dark:text-slate-500 truncate">{tpl.desc}</div>
                  </div>
                  <i className={`ri-arrow-${isOpen ? 'up' : 'down'}-s-line text-slate-400 flex-shrink-0`}></i>
                </button>

                {/* LaTeX preview (always visible) */}
                <div className="bg-slate-50 dark:bg-slate-800/60 border-t border-slate-100 dark:border-slate-700 px-4 py-2">
                  <MathRenderer content={`$${tpl.latex}$`} className="text-sm text-slate-700 dark:text-slate-300 text-center" />
                </div>

                {/* Expanded: fields + preview + buttons */}
                {isOpen && (
                  <div className="bg-white dark:bg-slate-800 border-t border-slate-100 dark:border-slate-700 px-4 pb-4 pt-3 space-y-3">
                    {/* Parameter fields */}
                    <div className="space-y-2">
                      {tpl.fields.map(field => (
                        <div key={field.key}>
                          <label className="text-[10px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wide block mb-1">
                            {field.label}
                          </label>
                          {field.type === 'select' ? (
                            <select
                              value={vals[field.key] ?? field.placeholder}
                              onChange={e => setField(tpl.id, field.key, e.target.value)}
                              className="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-teal-400 cursor-pointer"
                            >
                              {(field.options ?? []).map(opt => (
                                <option key={opt} value={opt}>{opt}</option>
                              ))}
                            </select>
                          ) : (
                            <input
                              type="text"
                              value={vals[field.key] ?? ''}
                              onChange={e => setField(tpl.id, field.key, e.target.value)}
                              placeholder={field.placeholder}
                              className="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-400 font-mono"
                            />
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Live query preview */}
                    <div className="bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800/50 rounded-xl px-3 py-2.5">
                      <div className="text-[9px] font-bold text-teal-600 dark:text-teal-400 uppercase tracking-wide mb-1">Query Preview</div>
                      <code className="text-xs text-teal-800 dark:text-teal-200 font-mono break-all">{builtQuery}</code>
                    </div>

                    {/* Action buttons */}
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => handleInsert(tpl)}
                        className="flex items-center justify-center gap-1.5 py-2.5 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 text-xs font-bold rounded-xl transition-all cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-edit-2-line text-sm"></i>
                        Insert
                      </button>
                      <button
                        onClick={() => handleSolve(tpl)}
                        className="flex items-center justify-center gap-1.5 py-2.5 bg-gradient-to-r from-teal-600 to-emerald-600 hover:opacity-90 text-white text-xs font-bold rounded-xl transition-all shadow-sm cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-flashlight-line text-sm"></i>
                        Solve
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 flex-shrink-0">
          <p className="text-[10px] text-slate-400 dark:text-slate-500 text-center">
            {CATEGORIES.reduce((a, c) => a + c.templates.length, 0)} formula templates · click a card to expand
          </p>
        </div>
      </div>
    </>
  );
}
